import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import {
  useGetEntitlements,
  useRefreshEntitlements,
  getEntitlementsWithFallback
} from '@utils/api/entitlements/use-get-entitlements.tsx';
import {
  Entitlements,
  FeatureFlags,
  PlanSku,
  ERROR_FALLBACK_ENTITLEMENTS,
  hasTopTierPlan,
  getUpgradeButtonState,
  UpgradeButtonState
} from '@utils/types/entitlements.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';

interface EntitlementsContextValue {
  /** Raw entitlements data */
  entitlements: Entitlements;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  isError: boolean;

  /** Check if a specific feature is enabled */
  hasFeature: (feature: keyof FeatureFlags) => boolean;

  /** Check if organization has any active plans */
  hasActivePlans: boolean;

  /** Check if organization has a top-tier plan (Business/Enterprise) */
  isTopTier: boolean;

  /** Upgrade button state: 'hidden', 'subscribe', or 'upgrade' */
  upgradeButtonState: UpgradeButtonState;

  /** List of active plan SKUs */
  activePlans: PlanSku[];

  /** Optional plan-level cap on concurrent realtime streams. Null = no cap. */
  realtimeMaxConcurrentStreams: number | null;

  /** Optional plan-level allow-list of realtime language codes. Null = all allowed. */
  realtimeLanguages: string[] | null;

  /**
   * Organization may not start new credit-consuming work. Use it to disable
   * those entry points up front rather than letting the API reject them.
   */
  uploadSuspended: boolean;

  /** Refresh entitlements (call after subscription changes) */
  refreshEntitlements: () => void;
}

const EntitlementsContext = createContext<EntitlementsContextValue | null>(
  null
);

export default function EntitlementsProvider(props: PropsWithChildren) {
  const { data, isLoading, isError } = useGetEntitlements();
  const refreshEntitlements = useRefreshEntitlements();

  const entitlements = useMemo(() => getEntitlementsWithFallback(data), [data]);

  const value = useMemo<EntitlementsContextValue>(() => {
    // In on-prem mode, return defaults with all features enabled
    if (IS_ON_PREM) {
      return {
        entitlements: ERROR_FALLBACK_ENTITLEMENTS,
        isLoading: false,
        isError: false,
        hasFeature: () => true,
        hasActivePlans: true,
        isTopTier: true,
        upgradeButtonState: 'hidden',
        activePlans: [],
        realtimeMaxConcurrentStreams: null,
        realtimeLanguages: null,
        uploadSuspended: false,
        refreshEntitlements
      };
    }

    const {
      features,
      active_plans,
      hide_upsell,
      realtime_max_concurrent_streams,
      realtime_languages,
      upload_suspended
    } = entitlements;

    return {
      entitlements,
      isLoading,
      isError,
      // ``publishing`` is intentionally NOT special-cased here — it is
      // driven by the organization setting ``publishing.enabled`` and
      // surfaced via ``useIsPublishingEnabled`` (which piggybacks on
      // the connections query cache). Callers should use that hook
      // instead of asking for ``hasFeature('publishing')``.
      hasFeature: (feature: keyof FeatureFlags) => features[feature] ?? false,
      hasActivePlans: active_plans.length > 0,
      isTopTier: hasTopTierPlan(active_plans),
      upgradeButtonState: getUpgradeButtonState(active_plans, hide_upsell),
      activePlans: active_plans,
      realtimeMaxConcurrentStreams: realtime_max_concurrent_streams ?? null,
      realtimeLanguages:
        realtime_languages && realtime_languages.length > 0
          ? realtime_languages
          : null,
      uploadSuspended: upload_suspended ?? false,
      refreshEntitlements
    };
  }, [entitlements, isLoading, isError, refreshEntitlements]);

  return (
    <EntitlementsContext.Provider value={value}>
      {props.children}
    </EntitlementsContext.Provider>
  );
}

/**
 * Hook to access entitlements context.
 * Must be used within an EntitlementsProvider.
 */
export const useEntitlements = (): EntitlementsContextValue => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error(
      'useEntitlements must be used within an EntitlementsProvider'
    );
  }
  return context;
};

/**
 * Hook to check if a specific feature is enabled.
 * Convenience wrapper around useEntitlements.
 */
export const useHasFeature = (feature: keyof FeatureFlags): boolean => {
  const { hasFeature } = useEntitlements();
  return hasFeature(feature);
};
